<template>
  <app-collapse
    heading="Security details"
    :is-open="true"
  >
    <app-card
      variant="muted"
      body-class="space-y-3"
    >
      <data-property
        label="User access"
        direction="horizontal"
        loading-skeleton-class="max-w-[82px]! rounded-full!"
        :is-loading="isLoading"
      >
        <template #value>
          <user-status-badge :status="getUserStatus(user)" />
        </template>
      </data-property>

      <data-property
        label="Multi-factor Authentication"
        direction="horizontal"
        loading-skeleton-class="max-w-[82px]! rounded-full!"
        :is-loading="isLoading"
      >
        <template #value>
          <user-multifactor-status-badge :status="getUserMultifactorStatus(user?.multifactor)" />
        </template>
      </data-property>

      <data-property
        label="Login count"
        :value="user?.logins_count || 0"
        direction="horizontal"
        :is-loading="isLoading"
      />

      <data-property
        label="Last login"
        direction="horizontal"
        :is-loading="isLoading"
        class="items-start"
      >
        <template #value>
          <div class="text-right">
            <p>{{ formatDate(user?.last_login, 'L LT') || 'N/A' }}</p>
            <div
              v-if="user?.last_ip"
              class="flex items-center justify-end gap-1 text-xs text-gray-600"
            >
              <app-tooltip
                heading="Last login IP address"
                :body="user.last_ip.length > 28 ? user.last_ip : ''"
              >
                <app-icon
                  name="info-circle"
                  size="sm"
                />
              </app-tooltip>
              <text-truncate
                :value="user.last_ip"
                :show-tooltip="false"
                :max-length="28"
              />
            </div>
          </div>
        </template>
      </data-property>

      <div class="flex gap-3 [&>*]:flex-1">
        <button
          v-if="canBlockUnblockUser && !user?.blocked"
          :disabled="isLoading || isFetching || isBlockingUser"
          class="btn btn-sm btn-secondary"
          @click="blockAccessModalRef.showModal()"
        >
          <app-icon
            v-if="isBlockingUser"
            name="loading"
            size="sm"
            class="animate-spin"
          />
          <app-icon
            v-else
            name="x-circle"
            size="sm"
          />
          Block access
        </button>
        <button
          v-if="canBlockUnblockUser && user?.blocked"
          :disabled="isLoading || isFetching || isUnblockingUser"
          class="btn btn-sm btn-secondary"
          @click="unblockAccessModalRef.showModal()"
        >
          <app-icon
            v-if="isUnblockingUser"
            name="loading"
            size="sm"
            class="animate-spin"
          />
          <app-icon
            v-else
            name="user-check"
            size="sm"
          />
          Reactivate user
        </button>
      </div>
    </app-card>
  </app-collapse>

  <confirmation-modal
    ref="blockAccessModalRef"
    heading="Block user access?"
    heading-icon="lock-one"
    color="error"
    :is-loading="isBlockingUser"
    :primary-action-copy="isBlockingUser ? 'Blocking...' : 'Yes, block access'"
    :confirmation-heading="`Are you sure you want to block ${userName} access?`"
    confirmation-subheading="If you block this user’s access, they won’t be able to log in again until an admin reactivates their account."
    @click:confirm="handleBlockUser"
  >
    <user-details-card
      :user="user"
      :roles="userRoles"
      :next-status="UserStatus.BLOCKED"
      card-border-color="error"
    />
  </confirmation-modal>

  <confirmation-modal
    ref="unblockAccessModalRef"
    heading="Reactivate user access?"
    heading-icon="user-check"
    color="primary"
    :is-loading="isUnblockingUser"
    :primary-action-copy="isUnblockingUser ? 'Reactivating...' : 'Yes, reactivate access'"
    :confirmation-heading="`Are you sure you want to reactivate ${userName} access?`"
    confirmation-subheading="If you reactivate this user’s access, they will be able to log in back again."
    @click:confirm="handleUnblockUser"
  >
    <user-details-card
      :user="user"
      :roles="userRoles"
      :next-status="UserStatus.ACTIVE"
      card-border-color="success"
    />
  </confirmation-modal>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useAuth0 } from '@auth0/auth0-vue';
import { useAuthStore } from '@/stores/auth';
import { formatDate } from '@/utils/dates';
import { getUserName, getUserStatus, getUserMultifactorStatus } from '@/utils/users';
import { useToast } from '@/composables/useToast';
import { UserStatus } from '@/constants/users';
import { useBlockUserById, useUnblockUserById } from '@/services/users/queries';
import UserDetailsCard from '@/components/users/UserDetailsCard.vue';
import ConfirmationModal from '@/components/ui/ConfirmationModal.vue';
import UserStatusBadge from './UserStatusBadge.vue';
import UserMultifactorStatusBadge from './UserMultifactorStatusBadge.vue';

const props = defineProps({
  user: {
    type: Object,
    default: () => ({}),
  },
  userRoles: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  isFetching: {
    type: Boolean,
    default: false,
  },
});

const blockAccessModalRef = ref(null);
const unblockAccessModalRef = ref(null);

const { toast } = useToast();
const authStore = useAuthStore();
const { user: auth0User } = useAuth0();
const { mutate: blockUser, isPending: isBlockingUser } = useBlockUserById();
const { mutate: unblockUser, isPending: isUnblockingUser } = useUnblockUserById();

const userName = computed(() => getUserName(props.user));
const isLoggedInUser = computed(() => props.user?.email === auth0User.value?.email);
const canBlockUnblockUser = computed(() => !isLoggedInUser.value && authStore.hasPermission('user:edit'));

const handleBlockUser = () => {
  blockUser(props.user.user_id, {
    onSuccess: () => {
      toast({
        heading: 'User access blocked',
        message: `${userName.value}'s' user access was blocked. Only an admin can reactivate this account.`,
        color: 'success',
        icon: 'check-circle',
      });
      blockAccessModalRef.value?.closeModal();
    },
  });
};

const handleUnblockUser = () => {
  unblockUser(props.user.user_id, {
    onSuccess: () => {
      toast({
        heading: 'User access reactivated',
        message: `${userName.value}'s user access was reactivated successfully.`,
        color: 'success',
        icon: 'check-circle',
      });
      unblockAccessModalRef.value?.closeModal();
    },
  });
};
</script>
